import type { StaffPromotion, StaffSegment } from '@unified-lk/api-contracts'

interface Props {
  promotions: StaffPromotion[]
  segments: StaffSegment[]
  onEdit: (p: StaffPromotion) => void
  onDelete: (id: number) => void
  deleting: number | null
}

function segmentName(id: number, segments: StaffSegment[]): string {
  const s = segments.find((seg) => seg.id === id)
  return s?.name ?? s?.title ?? `#${id}`
}

function resolveSegmentIds(p: StaffPromotion): number[] {
  if (p.segment_ids?.length) return p.segment_ids
  return p.segments?.map((s) => s.id) ?? []
}

function priorityClass(priority: number): string {
  if (priority >= 70) return 'bg-red-100 text-red-700'
  if (priority >= 30) return 'bg-amber-100 text-amber-700'
  return 'bg-slate-100 text-slate-600'
}

function promotionImageUrl(p: StaffPromotion): string | null {
  const imageUrl = typeof p.image_url === 'string' ? p.image_url.trim() : ''
  return imageUrl || null
}

export function PromotionsList({ promotions, segments, onEdit, onDelete, deleting }: Props) {
  if (promotions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <i className="bi bi-megaphone text-5xl text-slate-200" />
        <p className="mt-4 text-base font-medium text-slate-500">Акций пока нет</p>
        <p className="text-sm text-slate-400">Создайте первую акцию для пациентов</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3">ID</th>
            <th className="px-4 py-3">Название</th>
            <th className="px-4 py-3">Сегменты</th>
            <th className="px-4 py-3">Период</th>
            <th className="px-4 py-3">Приоритет</th>
            <th className="px-4 py-3">Статус</th>
            <th className="px-4 py-3 text-right">Действия</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {promotions.map((p) => {
            const segIds = resolveSegmentIds(p)
            const start = p.start_date ? new Date(p.start_date).toLocaleDateString('ru-RU') : 'сразу'
            const end = p.end_date ? new Date(p.end_date).toLocaleDateString('ru-RU') : '∞'
            const imageUrl = promotionImageUrl(p)

            return (
              <tr key={p.id} className="group hover:bg-sky-50/40 transition-colors">
                <td className="px-4 py-3 font-semibold text-slate-500">#{p.id}</td>
                <td className="px-4 py-3 max-w-[220px]">
                  <div className="flex items-center gap-3">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt=""
                        className="h-14 w-20 shrink-0 rounded-xl border border-slate-200 object-cover bg-slate-50"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-[10px] text-slate-400">
                        Без фото
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-900">{p.title}</div>
                      {p.description && (
                        <div className="mt-0.5 truncate text-xs text-slate-400">{p.description}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {segIds.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {segIds.map((id) => (
                        <span key={id} className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-[11px] font-medium text-indigo-700">
                          {segmentName(id, segments)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="italic text-slate-400 text-xs">Не привязано</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                  <div>{start}</div>
                  <div className="text-slate-400">{end}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${priorityClass(p.priority ?? 0)}`}>
                    {p.priority ?? 0}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {p.is_active ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                      <i className="bi bi-check-circle-fill" /> Активна
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-500">
                      <i className="bi bi-dash-circle" /> Неактивна
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => onEdit(p)}
                      title="Редактировать"
                      className="rounded-xl p-1.5 text-sky-600 hover:bg-sky-100 transition"
                    >
                      <i className="bi bi-pencil-fill text-base" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(p.id)}
                      disabled={deleting === p.id}
                      title="Удалить"
                      className="rounded-xl p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-40 transition"
                    >
                      {deleting === p.id
                        ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-red-400 border-t-transparent" />
                        : <i className="bi bi-trash-fill text-base" />
                      }
                    </button>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
